import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Divider,
  Grid,
  Stack,
  type SxProps,
  type Theme,
} from "~/common/mui";
import { useTranslation } from "react-i18next";
import { Parameter } from "~/common/components/ParameterV2";
import { Product } from "~/domains/product/Product";
import type { HousingProduct } from "~/domains/product/types/ProductData";
import type {
  ProductLineKitViewModel,
  ProductLineOptionalPartViewModel,
} from "../../cartV2/models/ProductTableViewModel";
import { OptionalPartEntry } from "../OptionalPartEntry";
import * as PriceUtils from "~/utils/PriceUtils";

export type ResolvedOptionalPart = ProductLineOptionalPartViewModel & {
  descriptor: NonNullable<ProductLineOptionalPartViewModel["descriptor"]>;
  viewModel: NonNullable<ProductLineOptionalPartViewModel["viewModel"]>;
};

interface KitPartsSectionProps {
  kit: ProductLineKitViewModel;
  editMode: boolean;
  renderOptionalPart?: (part: ResolvedOptionalPart) => React.ReactNode;
}

const IncludedPartTile = ({ housingProduct, kitQuantity }: { housingProduct: HousingProduct; kitQuantity: number }) => (
  <Product.Context value={housingProduct.product}>
    <Card component={Stack} direction={"row"} gap={2} sx={{ p: 2, height: "100%" }}>
      <Product.PrimaryImageWithLogosV2 size={64} gap={8} />
      <Stack gap={1}>
        <Product.DesignationV2 fontWeight={"bold"} />
        <Parameter label="Quantity" value={housingProduct.quantity * kitQuantity} />
      </Stack>
    </Card>
  </Product.Context>
);

const kitAccordionSx: SxProps<Theme> = (theme) => ({
  "&::before": { display: "none !important" },
  "&::after": { display: "none !important" },
  bgcolor: "common.white",
  transition: theme.transitions.create("background-color"),
  "&.Mui-expanded": {
    bgcolor: "surface.main",
  },
});

export const KitPartsSection = ({ kit, editMode, renderOptionalPart }: KitPartsSectionProps) => {
  const { t } = useTranslation();

  const hasIncludedParts = kit.includedParts.length > 0;

  return (
    <Card sx={{ bgcolor: "surface.main" }}>
      {hasIncludedParts && (
        <>
          <Accordion sx={kitAccordionSx}>
            <AccordionSummary>
              <Stack gap={1}>
                {t("page.cart.housing.included.title")}
                <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
                  <Parameter
                    variant="row"
                    label={t("cartPage.table.item", { count: kit.includedPartsTotalQuantity })}
                    value={kit.includedPartsTotalQuantity}
                  />
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container columns={{ phone: 2, "desktop-m": 4 }} gap={2}>
                {kit.includedParts.map((part) => (
                  <Grid key={part.part.product.designation} size={1}>
                    <IncludedPartTile
                      housingProduct={part.part}
                      kitQuantity={part.totalQuantity / part.part.quantity}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Divider flexItem />
        </>
      )}

      <Accordion sx={kitAccordionSx}>
        <AccordionSummary>
          <Stack gap={1}>
            {t("page.cart.housing.optional.title")}
            <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
              <Parameter
                variant="row"
                label={t("cartPage.table.item", { count: kit.optionalPartsTotalQuantity })}
                value={kit.optionalPartsTotalQuantity}
              />
              {kit.optionalPartsTotalPrice !== null && (
                <Parameter
                  variant="row"
                  label={t("cartPage.table.header.total.title")}
                  value={<strong>{PriceUtils.text(kit.optionalPartsTotalPrice)}</strong>}
                />
              )}
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={2}>
            {kit.optionalParts
              .filter((op): op is ResolvedOptionalPart => !!op.descriptor && !!op.viewModel)
              .map((optionalPart) =>
                renderOptionalPart ? (
                  renderOptionalPart(optionalPart)
                ) : (
                  <OptionalPartEntry
                    key={optionalPart.rowId}
                    part={optionalPart.part}
                    descriptor={optionalPart.descriptor}
                    viewModel={optionalPart.viewModel}
                    actions={null}
                    editMode={editMode}
                  />
                ),
              )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};
